<template>
  <div class="card space-y-4">
    <div>
      <h2 class="text-lg font-medium m-0">Manual capacity entry</h2>
      <p class="mt-1 mb-0 text-xs text-gray-500">
        Enter weekly capacity for one vendor and type. Dates snap to the Monday of their week; weeks that already have allocations are kept unless you overwrite.
      </p>
    </div>

    <div class="grid gap-4 sm:grid-cols-3">
      <div>
        <label class="label" for="manual-vendor">Vendor</label>
        <select id="manual-vendor" v-model="vendorId" class="select">
          <option value="" disabled>Select vendor</option>
          <option v-for="vendor in vendorOptions" :key="vendor.id" :value="vendor.id">
            {{ vendor.alias ? `${vendor.alias} — ${vendor.name}` : vendor.name }}
          </option>
        </select>
      </div>
      <div>
        <label class="label" for="manual-type">Type</label>
        <select id="manual-type" v-model="capacityTypeId" class="select" :disabled="vendorId === ''">
          <option value="" disabled>Select type</option>
          <optgroup v-if="terminationTypeOptions.length" label="Termination">
            <option v-for="type in terminationTypeOptions" :key="type.id" :value="type.id">{{ type.code }} — {{ type.label }}</option>
          </optgroup>
          <optgroup v-if="moldingTypeOptions.length" label="Molding">
            <option v-for="type in moldingTypeOptions" :key="type.id" :value="type.id">{{ type.code }} — {{ type.label }}</option>
          </optgroup>
        </select>
        <p v-if="vendorId !== '' && !loadingCapacityTypes && availableCapacityTypes.length === 0" class="mt-1 mb-0 text-xs text-amber-600">
          This vendor has no capacity types configured yet — add them on the Vendor Settings page first.
        </p>
      </div>
      <div>
        <label class="label" for="manual-factor">Capacity factor (optional)</label>
        <input id="manual-factor" v-model.number="capacityFactor" type="number" step="0.05" min="0" max="1" placeholder="—" class="input tabular-nums" />
      </div>
    </div>

    <div class="space-y-2">
      <p class="label m-0">Weeks</p>
      <div v-for="(week, index) in weeks" :key="index" class="flex flex-wrap items-center gap-3">
        <input v-model="week.startDate" type="date" class="input w-44" aria-label="Week start date" />
        <input v-model.number="week.capacity" type="number" min="0" placeholder="Capacity" class="input w-36 text-right tabular-nums" aria-label="Capacity" />
        <button class="btn-secondary px-2.5" type="button" :disabled="weeks.length === 1" aria-label="Remove week" @click="removeWeek(index)">×</button>
      </div>
      <button class="btn-secondary" type="button" @click="addWeek">Add week</button>
    </div>

    <label class="flex items-center gap-2 text-sm text-gray-700">
      <input v-model="overwriteExisting" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
      Overwrite existing allocations for these weeks (planned POs stay assigned; overwritten weeks may become overbooked)
    </label>

    <div class="flex items-center gap-3">
      <button class="btn-primary" type="button" :disabled="saving || !canSubmit" @click="submit">
        {{ saving ? 'Saving…' : 'Save capacity allocations' }}
      </button>
      <p v-if="!canSubmit" class="m-0 text-sm text-gray-500">Pick a vendor, a type, and fill every week row.</p>
    </div>

    <div v-if="error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ error }}</div>
    <div v-if="resultSummary" class="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
      {{ resultSummary }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { createManualAllocations } from '@/services/capacityCalendarService';
  import { useVendorAvailableCapacityTypes } from '@/composables/useVendorCapacityTypes';
  import { useAccess } from '@/composables/useAccess';
  import { useVendorStore } from '@/store/vendorStore';
  import type { ManualAllocationsResult } from '@/types/capacityImport';
  import type { CapacityType } from '@/types/capacityType';
  import type { VendorSummary } from '@/types/vendor';
  import { extractErrorMessage } from '@/utils/apiError';

  const props = defineProps<{ capacityTypes: CapacityType[] }>();

  const vendorStore = useVendorStore();
  const { hasAllVendors, scopedVendors } = useAccess();

  // All-vendor callers source the active vendor directory (populated by the parent page);
  // scoped callers source from the vendors scoped to their account on /me instead.
  const vendorOptions = computed<VendorSummary[]>(() => (hasAllVendors.value ? vendorStore.activeVendors : scopedVendors.value));

  const vendorId = ref('');
  const capacityTypeId = ref('');
  const { availableTypes: availableCapacityTypes, loading: loadingCapacityTypes } = useVendorAvailableCapacityTypes(
    () => vendorId.value,
    () => props.capacityTypes,
  );

  // Grouped for the <optgroup> select below — rendered only when non-empty so a
  // vendor offering just one category doesn't show an empty second group.
  const terminationTypeOptions = computed(() => availableCapacityTypes.value.filter((type) => type.category === 'termination'));
  const moldingTypeOptions = computed(() => availableCapacityTypes.value.filter((type) => type.category === 'molding'));

  // The previous vendor's selection never applies to a different vendor's type list.
  watch(vendorId, () => {
    capacityTypeId.value = '';
  });

  // A partner scoped to exactly one vendor has no real choice to make — preselect it so the
  // form doesn't sit on the disabled placeholder for a picker with only one possible answer.
  watch(
    vendorOptions,
    (options) => {
      if (!hasAllVendors.value && options.length === 1 && vendorId.value === '') vendorId.value = options[0].id;
    },
    { immediate: true },
  );
  const capacityFactor = ref<number | ''>('');
  const overwriteExisting = ref(false);
  const weeks = ref<{ startDate: string; capacity: number | '' }[]>([{ startDate: '', capacity: '' }]);
  const saving = ref(false);
  const error = ref<string | null>(null);
  const lastResult = ref<ManualAllocationsResult | null>(null);

  const canSubmit = computed(
    () =>
      vendorId.value !== '' &&
      capacityTypeId.value !== '' &&
      weeks.value.length > 0 &&
      weeks.value.every((week) => week.startDate !== '' && week.capacity !== '' && week.capacity >= 0)
  );

  const resultSummary = computed(() => {
    if (!lastResult.value) return '';
    const { created, updated, skipped } = lastResult.value;
    const kept = skipped > 0 ? ` · ${skipped} week(s) kept (already planned — check overwrite to replace them)` : '';
    return `Saved: ${created} allocation(s) created, ${updated} updated${kept}`;
  });

  function addWeek(): void {
    weeks.value.push({ startDate: '', capacity: '' });
  }

  function removeWeek(index: number): void {
    if (weeks.value.length > 1) weeks.value.splice(index, 1);
  }


  async function submit(): Promise<void> {
    if (!canSubmit.value) return;
    saving.value = true;
    error.value = null;
    lastResult.value = null;
    try {
      lastResult.value = await createManualAllocations({
        vendorId: vendorId.value,
        capacityTypeId: capacityTypeId.value,
        capacityFactor: capacityFactor.value === '' ? null : capacityFactor.value,
        overwriteExisting: overwriteExisting.value,
        weeks: weeks.value.map((week) => ({ startDate: week.startDate, capacity: Number(week.capacity) })),
      });
    } catch (saveError) {
      error.value = extractErrorMessage(saveError);
    } finally {
      saving.value = false;
    }
  }
</script>
